import { useCallback, useMemo } from "react";
import { useLivePreview } from "@payloadcms/live-preview-react";
import { getPayloadCmsUrl } from "src/api/payload/client";
import { mapEntertainmentEvent } from "src/utils/mapEntertainment";
import { entertainmentLivePreviewRequestHandler } from "src/utils/entertainmentLivePreviewHandler";

/**
 * Merges live entertainment card data into the hub list.
 *
 * @param {import('src/utils/mapEntertainment').EntertainmentCard[]} events
 * @param {boolean} enabled - true when REACT_APP_PROMOTIONS_SOURCE=payload
 * @param {string} [locale] - `en` | `fr`
 */
export function useEntertainmentLivePreview(events, enabled, locale) {
  const initialDoc = useMemo(() => ({}), []);
  const requestHandler = useCallback(entertainmentLivePreviewRequestHandler, []);

  const { data: liveDoc, isLoading } = useLivePreview({
    initialData: initialDoc,
    serverURL: getPayloadCmsUrl(),
    depth: 1,
    requestHandler,
  });

  const mergedEvents = useMemo(() => {
    if (!enabled || !liveDoc?.name) return events;

    const liveCard = mapEntertainmentEvent(liveDoc, locale);

    const index = events.findIndex((e) => e.name === liveCard.name);
    if (index >= 0) {
      const next = [...events];
      next[index] = { ...next[index], ...liveCard };
      return next;
    }

    return [...events, liveCard];
  }, [events, liveDoc, enabled, locale]);

  return {
    events: mergedEvents,
    isLivePreview: Boolean(liveDoc?.name),
    livePreviewName: liveDoc?.name,
    isLoadingLivePreview: isLoading,
  };
}
